import { Component, OnInit, EventEmitter, Input, Output } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { environment } from 'src/environments/environment';
import { CoreService, DocumentService, LoaderService, PatientFormsService, PortalService, SecurityService, UserService } from 'src/app/services';
import { DocumentType, IdentityUserAccount, IESignInputModel, IESignResponse, IPortalConfig, NotificationType, UserAccount } from 'src/app/models';
import { TopNotificationService } from '../top-notification/top-notification.service';
import { Helpers } from 'src/app/helpers';
import { TrainingService } from 'src/app/services/training.service';
import { CommunicationService } from 'src/app/services/communication.service';
import { Guid } from 'guid-typescript';

@Component({
    selector: 'medpt-epa-signature',
    templateUrl: './epa-signature.component.html',
    styleUrls: ['./epa-signature.component.scss'],
    standalone: false
})
export class EpaSignatureComponent implements OnInit {
  esignform: FormGroup;
  cdnEndPoint: any = environment.cdnEndPoint;
  submitted: boolean = false;
  private currentUser: UserAccount;
  fullName: string = '';
  userName: string = '';
  description: string = "";
  contentTitle: string = "";
  title: string = "";
  @Input() eSignInputModel!: IESignInputModel;
  @Output() updateSignatureEvent: EventEmitter<any> = new EventEmitter();
  portalInfo: IPortalConfig;
  warningMsg: string = "";
  showWarning: boolean = false;
  sponsor!: string;
  isExternalLogin:boolean =false;
  constructor(
    public modal: NgbActiveModal,
    private formBuilder: FormBuilder,
    private securityService: SecurityService,
    private notificationService: TopNotificationService,
    private loaderService: LoaderService,
    private portalService: PortalService,
    private trainingService: TrainingService,
    private documentService: DocumentService,
    private communicationService: CommunicationService,
    private pfService: PatientFormsService,
    private userService: UserService,
    private coreService: CoreService
  ) {
    this.currentUser = this.securityService.currentUserValue;
    this.isExternalLogin = this.currentUser.Idp === "local" ? false : true; 
    this.fullName = Helpers.decodeEntity(this.currentUser.FirstName + ' ' + this.currentUser.LastName);
    this.userName = Helpers.decodeEntity(this.currentUser.UserName);
    this.esignform = this.formBuilder.group({
      password: ['', Validators.required]
    });
    this.portalInfo = this.portalService.portalConfiguation.getValue();
    this.sponsor = this.portalService.portalConfiguation?.value.SponsorName!;
  }

  ngOnInit(): void {
    this.title = this.eSignInputModel.Title;
    let dt = this.eSignInputModel.DocumentType;
    switch (this.eSignInputModel.DocumentType) {
      case DocumentType.Training:
        this.checkMultipleStudies();
        this.description = "I attest that I have viewed this training.";
        this.contentTitle = "Training Info";
        this.warningMsg = "This training is assigned to you in other studies. Upon signature, it will be marked as completed across all studies.";
        break;
      case DocumentType.Document:
        this.checkMultipleStudies();
        this.description = "I attest that I have viewed this document.";
        this.contentTitle = "Document Info";
        this.warningMsg = "This document is assigned to you in other studies. Upon signature, it will be marked as completed across all studies.";
        break;
      case DocumentType.SafetyReport:
        this.description = "My electronic signature is to acknowledge my review of this safety report.";
        this.contentTitle = "Safety Report Info"; 
        this.warningMsg = "This Safety report is assigned to you in other studies. Upon signature, it will be marked as completed across all studies.";
        this.showWarning = this.eSignInputModel.HasMultiplePortalAssignments;
        break;
      case DocumentType.Survey:
        this.description = "I attest that, to the best of my knowledge, the provided information is accurate.";
        this.contentTitle = "Survey Info";
        break;
      default:
        this.description = "";
        this.contentTitle = "";
        break;
    }
  }
  ngOnDestroy() {
    this.updateSignatureEvent.unsubscribe();
   }
  onSubmitEpaSign() {
    this.submitted = true;
    if (this.esignform.valid) {
      this.loaderService.showLoader();
      let postdata = { UserName: this.currentUser.UserName, Password: this.esignform.get("password")?.value, MedPointPlatformId: Helpers.getPlatformId() };

      this.userService.AuthenticateSigningUser(postdata).then((response: IdentityUserAccount) => {
        if (response.Success) {

          let promiseData: Promise<any> = new Promise(() => { });
          if (this.eSignInputModel.DocumentType == DocumentType.Training)
            promiseData = this.trainingService.PostEpaSignature(<string>this.portalInfo.SponsorName, <number>this.portalInfo.PortalID, this.eSignInputModel.PersonAssetGuid);
          else if (this.eSignInputModel.DocumentType == DocumentType.Document)
            promiseData = this.documentService.PostEpaSignature(<string>this.portalInfo.SponsorName, <number>this.portalInfo.PortalID, this.eSignInputModel.PersonAssetGuid);
          else if (this.eSignInputModel.DocumentType == DocumentType.SafetyReport)
            promiseData = this.communicationService.postEpaSignature(this.eSignInputModel.AssetId);
          else if (this.eSignInputModel.DocumentType == DocumentType.Survey)
            promiseData = this.pfService.postEpaSignature(<string>this.portalInfo.SponsorName, <number>this.portalInfo.PortalID, this.eSignInputModel.PersonAssetGuid);

          promiseData.then((signresult: IESignResponse) => {
            if (signresult.Success) {
              this.notificationService.showNotification("Electronic Signature successfully completed.", NotificationType.Success);
              this.updateSignatureEvent.next(true);
              this.modal.close(true);
              this.loaderService.hideLoader();
            } 
            else if(!signresult.Success && signresult.Message != ''){
              this.loaderService.hideLoader();
              this.notificationService.showNotification(signresult.Message, NotificationType.Error);
            }
              else {
                this.loaderService.hideLoader();
              this.notificationService.showNotification("An error occured during eSign.", NotificationType.Error);
            }
          }).finally(() => {
           
          });

        } else {
          this.loaderService.hideLoader();
          this.notificationService.showNotification(response.Message == "User not found." ? "Invalid username or password." : response.Message, NotificationType.Error);
        }

      }, (error: any) => {
        this.notificationService.showNotification(error.error.Message, NotificationType.Error);
        this.loaderService.hideLoader();
      }).finally(() => {
      });
    }
  }
  checkMultipleStudies() {
    this.loaderService.showLoader();
    this.coreService.GetIsAssetAssignedInMultiplePortals(this.sponsor, this.eSignInputModel.AssetId).then((response: any) => {
      this.showWarning = response;
    }).finally(() => {
      this.loaderService.hideLoader();
    });
  }
  onSignClick(){
    this.submitted = true;
    this.loaderService.showLoader();
    const selectedAssetIds: string[] = [];
    var signingId = "signingId_" +  Guid.create().toString();
    if (this.eSignInputModel.DocumentType == DocumentType.Training || this.eSignInputModel.DocumentType == DocumentType.Document || this.eSignInputModel.DocumentType == DocumentType.Survey)
      selectedAssetIds.push(this.eSignInputModel.PersonAssetGuid);
    else if (this.eSignInputModel.DocumentType == DocumentType.SafetyReport)
      selectedAssetIds.push(this.eSignInputModel.AssetId.toString()); 
    const signingData = {'selectedAssetIds':selectedAssetIds, 'selectedTypeId':this.eSignInputModel.selectedTypeId} 
    localStorage.setItem(signingId, JSON.stringify(signingData));
    var queryString = "?signingid="+ signingId+"$"+this.eSignInputModel.AssetId+"$"+this.eSignInputModel.showDocument;
    var returnUrl = window.location.href.split('?').length > 1  ? 
                    window.location.href.split('?')[0] + queryString : 
                    window.location.href + queryString;
    var externalUrl = environment.SSO.secureAppUrl + "/Account/ExternalLogin?provider="+this.currentUser.Idp +"&returnUrl=" + returnUrl;
    window.location.href = externalUrl;
  }
}
